/**
 * Tarjeta del Inicio que abre la bandeja de autorizaciones (solo AdmG,
 * igual que la pantalla Solicitudes del celular).
 *
 * El contador de pendientes se refresca al instante con los eventos de
 * Firebase (módulo "Autorizaciones", los publican el celular, la web y la
 * transferencia de chasis) y cada minuto por si el evento no llega: las
 * pantallas nuevas crean la autorización sin publicar nada.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP } from "components/financepro";
import { useApi } from "lib/api";
import { escucharEventosNegocio } from "lib/firebaseNegocio";
import BandejaAutorizaciones from "components/autorizaciones/BandejaAutorizaciones";

const CADA_MS = 60 * 1000;

export default function AvisoAutorizaciones() {
  const [{ roleWeb }] = useMaterialUIController();
  const esAdmG = String(roleWeb || "").trim() === "AdmG";
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();

  const [pendientes, setPendientes] = useState(0);
  const [abierta, setAbierta] = useState(false);
  const [refrescar, setRefrescar] = useState(0);
  const previo = useRef(null);

  const actualizarConteo = useCallback(
    (n) => {
      // Aviso solo cuando aumenta y la bandeja está cerrada (no al cargar).
      if (previo.current !== null && n > previo.current && !abierta) {
        showAlert({
          message:
            n - previo.current === 1
              ? "Hay una autorización nueva esperando respuesta"
              : `Hay ${n - previo.current} autorizaciones nuevas esperando respuesta`,
          color: "info",
        });
      }
      previo.current = n;
      setPendientes(n);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [abierta]
  );

  const consultar = useCallback(async () => {
    const { ok, data } = await post("/api/autorizaciones_bandeja", { soloconteo: true });
    if (ok) actualizarConteo(data.pendientes || 0);
  }, [post, actualizarConteo]);

  useEffect(() => {
    if (!esAdmG) return undefined;
    consultar();
    const id = setInterval(consultar, CADA_MS);
    return () => clearInterval(id);
  }, [esAdmG, consultar]);

  useEffect(() => {
    if (!esAdmG) return undefined;
    return escucharEventosNegocio((ev) => {
      if (ev.Modulo !== "Autorizaciones") return;
      consultar();
      setRefrescar((k) => k + 1);
    });
  }, [esAdmG, consultar]);

  if (!esAdmG) return null;
  const hay = pendientes > 0;

  return (
    <>
      <ButtonBase
        onClick={() => setAbierta(true)}
        sx={{
          mb: 4,
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1.6,
          p: 2,
          borderRadius: 2.5,
          textAlign: "left",
          bgcolor: hay ? FP.color.warningSoft : FP.color.cardBg,
          border: `1px solid ${hay ? "#FCD34D" : "#E5E9F0"}`,
          boxShadow: FP.shadow.soft,
          transition: "all .15s ease",
          "&:hover": {
            borderColor: hay ? FP.color.warning : FP.color.navy,
            transform: "translateY(-2px)",
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: hay ? FP.color.warning : `${FP.color.navy}12`,
            color: hay ? "#FFFFFF" : FP.color.navy,
            flexShrink: 0,
          }}
        >
          <FactCheckOutlinedIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: FP.color.textPrimary }}>
            Autorizaciones
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: hay ? "#92400E" : FP.color.textSecondary }}>
            {pendientes === 0 && "No hay solicitudes esperando respuesta"}
            {pendientes === 1 && "1 solicitud esperando tu respuesta"}
            {pendientes > 1 && `${pendientes} solicitudes esperando tu respuesta`}
          </Typography>
        </Box>
        {hay && (
          <Box
            sx={{
              minWidth: 30,
              height: 30,
              px: 1,
              borderRadius: 999,
              bgcolor: FP.color.warning,
              color: "#FFFFFF",
              fontSize: "0.85rem",
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {pendientes > 99 ? "99+" : pendientes}
          </Box>
        )}
        <ChevronRightRoundedIcon sx={{ color: FP.color.textMuted }} />
      </ButtonBase>

      <Dialog fullScreen open={abierta} onClose={() => setAbierta(false)}>
        <BandejaAutorizaciones
          onExit={() => setAbierta(false)}
          onCambio={actualizarConteo}
          refrescarToken={refrescar}
        />
      </Dialog>
    </>
  );
}
